import { TK_API_PROJECTS } from '@/config';
import { resetBaseURL, downloadFile } from '@/lib/axios-v1';

import {
  GenericObject as GO,
  PAGESTATUS,
  PROJECTSTATE,
  SymbolEditParams,
} from '../types';

/**
 * Export vendor needed estimation result by project
 *
 * Method: GET
 * URL: projects/<project_id>/integration-exports/<target_system_id>
 *
 * @date 2025/10/10
 * @param editCtx
 * @param system_id
 * @returns
 */
export const exportProjectForVendor = async (
  editCtx: SymbolEditParams,
  system_id: string,
  params: string = '',
) => {
  const { projectId: pId } = editCtx;
  const reportType = `integration-exports/${system_id}${params}`;
  const singleExportURL = `${TK_API_PROJECTS}/${pId}/${reportType}`;
  const file = (await downloadFile(singleExportURL)) as any;
  return file as Blob;
};

/**
 * Export vendor needed estimation result
 *
 * @date 2025/08/06, 2026/01/22
 * @param editCtx drawing context
 * @param system_id aka vendor id: fast_duct | wendes | b2 | fast_plain
 * @param params option parameters such as: ?grouped=false&segment_size=5&format=xls
 * @returns
 */
export const exportDrawingForVendor = async (
  editCtx: SymbolEditParams,
  system_id: string,
  params: string = '',
) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const reportType = `integration-exports/${system_id}${params}`;
  const singleExportURL = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/${reportType}`;
  const file = (await downloadFile(singleExportURL)) as any;
  return file as Blob;
};

/**
 * Export `Marked up drawing` PDF file
 * Method: GET
 * URL: projects/<project_id>/blueprints/<blueprint_id>/pages/<page_id>/jobs/<job_id>/view-items-pdf
 * @2025/07/10
 */
export const exportMarkedUpDrawing = async (editCtx: SymbolEditParams) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const singleExportURL = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/view-items-pdf`;
  const file = (await downloadFile(singleExportURL)) as any;
  return file as Blob;
};

/**
 * Fetch file Blob of current drawing or project
 * @date add project export with multi-sheet
 * @param editCtx report source
 * @param format file format: CSV | EXCEL | PROJECT | xls
 * @param layout optional, single-column | multi-column
 * @param group group or not
 * @returns file Blob
 */
export const exportDrawingReport = async (
  editCtx: SymbolEditParams,
  format = 'EXCEL',
  layout?: string,
  group = true,
  segment_size = 1,
) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const parmas = `format=${format}&layout=${layout}&grouped=${group}&segment_size=${segment_size}`;
  const singleExportURL = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/summary?${parmas}`;
  const file = (await downloadFile(singleExportURL)) as any;
  return file as Blob;
};

export const exportSEVDrawing = async (
  editCtx: SymbolEditParams,
  format = 'xls',
  extCol = 'Stock_item',
) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const params = `format=${format}&extra_columns=${extCol}`;
  const singleExportURL = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/summary?${params}`;
  const file = (await downloadFile(singleExportURL)) as any;
  return file as Blob;
};

export const exportProjectReport = async (
  editCtx: SymbolEditParams,
  format = 'xls',
  layout?: string,
) => {
  const { projectId: pId } = editCtx;
  const projectExportURL = `${TK_API_PROJECTS}/${pId}/summary?format=${format}&layout=${layout}`;
  const file = (await downloadFile(projectExportURL)) as any;
  return file as Blob;
};

export const exportSEVProject = async (
  editCtx: SymbolEditParams,
  format = 'xls',
  extCol = 'Stock_item',
) => {
  const { projectId: pId } = editCtx;
  const params = `format=${format}&extra_columns=${extCol}`;
  const projectExportURL = `${TK_API_PROJECTS}/${pId}/summary?${params}`;
  const file = (await downloadFile(projectExportURL)) as any;
  return file as Blob;
};

export const exportInsulationDrawing = async (
  editCtx: SymbolEditParams,
  format = 'xls',
) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const url = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/summary/insulation?format=${format}`;
  const file = (await downloadFile(url)) as any;
  return file as Blob;
};

export const exportInsulationProject = async (
  editCtx: SymbolEditParams,
  format = 'xls',
) => {
  const { projectId: pId } = editCtx;
  const url = `${TK_API_PROJECTS}/${pId}/summary/insulation?format=${format}`;
  const file = (await downloadFile(url)) as any;
  return file as Blob;
};

export const fetchDrawingSummary = async (editCtx: SymbolEditParams) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const url = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/summary?format=JSON`;
  const result = (await resetBaseURL().get(url)) as any[];
  return result;
};

/**
 * Poundage summary exports for project and job:
 * Method: GET
 * URL: {{url}}projects/{{id_project}}/blueprints/{{id_blueprint}}/pages/{{id_page}}/jobs/{{id_job}}/summary/poundage?format=xls&grouped=false
 * format = xls | csv
 * grouped= false | true
 */
export const exportPoundageSummary = async (
  editCtx: SymbolEditParams,
  format = 'xls',
  grouped = 'false',
) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const url = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/summary/poundage?format=${format}&grouped=${grouped}`;
  const file = (await downloadFile(url)) as any;
  return file as Blob;
};

/**
 * Poundage summary exports for a project (we need a new menu: Poundage by project)
 */
export const exportPoundageProject = async (
  editCtx: SymbolEditParams,
  format = 'xls',
  grouped = 'false',
) => {
  const { projectId: pId } = editCtx;
  const url = `${TK_API_PROJECTS}/${pId}/summary/poundage?format=${format}&grouped=${grouped}`;
  const file = (await downloadFile(url)) as any;
  return file as Blob;
};

/**
 * Pipes summary export for a page:
 * Method: GET
 * URL: {{url}}projects/{id_project}/blueprints/{id_blueprint}/pages/{id_page}/jobs/{job_id}/summary/pipes?format=xls
 */
export const exportPipesForPage = async (
  editCtx: SymbolEditParams,
  format = 'xls',
) => {
  const { projectId: pId, fileId: bId, pageId: dId, jobId: jId } = editCtx;
  const url = `${TK_API_PROJECTS}/${pId}/blueprints/${bId}/pages/${dId}/jobs/${jId}/summary/pipes?format=${format}`;
  const file = (await downloadFile(url)) as any;
  return file as Blob;
};

/**
 * Pipes summary export for a project:
 * Method: GET
 * URL: {{url}}projects/{id_project}/summary/pipes?format=xls
 */
export const exportPipesForProject = async (
  editCtx: SymbolEditParams,
  format = 'xls',
) => {
  const { projectId: pId } = editCtx;
  const url = `${TK_API_PROJECTS}/${pId}/summary/pipes?format=${format}`;
  const file = (await downloadFile(url)) as any;
  return file as Blob;
};
